import { Component, Input, ViewChild, TemplateRef, signal, WritableSignal, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { DestinationEndpoint } from 'src/app/models/destination-endpoint';
import { ClientIntegrationService } from 'src/app/services/client-integration.service';
import { IntegrationRunTimeService } from 'src/app/services/integration-runtime.service';
import { IntegrationPushBackService } from 'src/app/services/integration-pushback.service';
import { TypeConstantService } from 'src/app/services/type-constant.service';
import { Integration } from '../../models/integration';
import { ClientInformation } from 'src/app/models/client-information';
import { ToastService } from 'src/app/services/toast.service';
import { User } from 'src/app/models/user';
import { IdentifierService } from 'src/app/services/identifier.service';
import { IntegrationRunTime } from 'src/app/models/integration-runtime';
import { IntegrationPushBack } from 'src/app/models/integration-pushback';
import { IntegrationInformation } from 'src/app/models/integration-information';

@Component({
  selector: 'app-client-pushbacks',
  standalone: false,
  templateUrl: './client-pushbacks.component.html',
  styleUrl: './client-pushbacks.component.scss',
})
export class ClientPushbacksComponent implements OnInit, OnDestroy {

  @Input() clientInformationId: string = null;
  @Input() information: IntegrationInformation = null;
  @Input() isInTab = false;
  componentDestroyed$: Subject<boolean> = new Subject();
  public loadingFailed: WritableSignal<boolean> = signal<boolean>(false);
  public gettingData: WritableSignal<boolean> = signal<boolean>(false);
  public clientDataReady: WritableSignal<boolean> = signal<boolean>(false);
  public searchedByIdentifier: WritableSignal<boolean> = signal<boolean>(false);

  @ViewChild('deletePushBackDialog', { static: true }) deletePushBackDialog: TemplateRef<any>;
  @ViewChild('editPushBackDialog', { static: true }) editPushBackDialog: TemplateRef<any>;

  selectedDestination: WritableSignal<DestinationEndpoint> = signal<DestinationEndpoint>(null);

  activeInformation: WritableSignal<IntegrationInformation> = signal<IntegrationInformation>(null);
  activeIntegration: WritableSignal<Integration> = signal<Integration>(null);

  //#region UserAllowedDestinations
  userDestinations: string[] = this.getUserAllowedDestinations();
  private getUserAllowedDestinations(): string[] {
    return this.user.getUserAllowedDestinations();
  }
  //#endregion

  selectedPushBack: IntegrationPushBack; // for delete
  selectedRunTime: IntegrationRunTime; // for delete
  activeClientInformationId: WritableSignal<string> = signal<string>(null);
  identifierIdValue: WritableSignal<string> = signal<string>(null);
  identifierNameValue: WritableSignal<string> = signal<string>(null);
  destinationId: WritableSignal<number> = signal<number>(null);
  public destinationName: WritableSignal<string> = signal<string>(null);

  public pushBacks: WritableSignal<IntegrationPushBack[]> = signal<IntegrationPushBack[]>(null);
  public informations: WritableSignal<IntegrationInformation[]> = signal<IntegrationInformation[]>(null);
  public runTimes: WritableSignal<IntegrationRunTime[]> = signal<IntegrationRunTime[]>(null);

  constructor(
    protected typeService: TypeConstantService,
    protected toastService: ToastService,
    protected idService: IdentifierService,
    private clientService: ClientIntegrationService,
    private runTimeService: IntegrationRunTimeService,
    private pushBackService: IntegrationPushBackService,
    private user: User,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
  ) {
    this.clientDataReady.set(false);

  }

  ngOnInit(): void {
    this.setupParams();

    if (this.clientInformationId && this.information) {
      this.activeClientInformationId.set(this.clientInformationId);

      this.activeInformation.set(this.information);
      this.destinationId.set(this.information.destinationId);
      this.identifierIdValue.set(this.information.identifierIdValue);
      this.identifierNameValue.set(this.information.identifierNameValue);
      if (this.information.destinationName != null && this.information.destinationName != undefined) {
        this.destinationName.set(this.information.destinationName);
      }
    }

    if (this.activeInformation() == null && this.activeClientInformationId() != null) {
      this.resolveInformationFromId(this.activeClientInformationId());
    }
    else if (this.activeInformation() != null && this.activeClientInformationId() == null) {
      this.resolveClientFromInformation(this.activeInformation());
    }
    else {
      this.loadPushbacks();
    }
  }

  ngOnDestroy(): void {
    this.gettingData.set(false);
    this.clientDataReady.set(false);
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  public setLoadingFailed(event: any): void {
    if (event == true || event == 'true') {
      this.loadingFailed.set(true);
    }
  }

  checkAccess(role: string[]): boolean {
    //User requires DevOps.Write or Admin
    return this.user.checkAccess(role);
  }

  private setupParams(): void {
    this.route.params
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(params => {
        if (params['clientInfoId'] != null) {
          this.activeClientInformationId.set(params['clientInfoId']);
        }
        if (params['identifierId'] != null) {
          this.identifierIdValue.set(params['identifierId']);
        }
        if (params['destName'] != null) {
          this.destinationName.set(params['destName']);
        }
        if (params['identifierName'] != null) {
          this.identifierNameValue.set(params['identifierName']);
        }
      });
  }

  private loadPushbacksByIntegration(): void {

    this.gettingData.set(true);
    this.searchedByIdentifier.set(false);

    this.pushBackService.getPushBackDataByIntegrations([this.activeIntegration().integrationId])
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (pushBackResponse) => {
          this.clientDataReady.set(true);
          var runTimes = pushBackResponse.runTimes.filter(rT => this.userDestinations.indexOf(rT.destinationEndpointName) > -1);
          var pushBacks = [];
          runTimes.forEach(runTime => {
            pushBacks = [...pushBacks, ...pushBackResponse.pushBacks.filter(pB => pB.integrationRunTimeId == runTime.integrationRunTimeId)];
          });
          this.runTimes.set(runTimes);
          this.pushBacks.set(pushBacks);
          this.gettingData.set(false);
        },
        error: (_error) => {
          this.toastService.toastCreate('Failed to load integration Pushbacks for ' + this.activeIntegration().name, 'Danger', {
            autoClose: false,
          });
        },
      });
  }

  private loadPushbacks(): void {
    this.clientDataReady.set(false);
    const clientInformationId = this.activeClientInformationId();
    const information = this.activeInformation();

    if (!clientInformationId || !information) return;
    this.clientDataReady.set(true);
    this.gettingData.set(true);
    this.searchedByIdentifier.set(true);

    const destinationEndpointId = information.destinationId;

    this.idService.getIntegrationInformationsByClientInformation(clientInformationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (informations) => {
          const matchedInformations = informations.filter(id =>
            id.identifierIdValue === information.identifierIdValue &&
            id.identifierNameValue === information.identifierNameValue &&
            id.destinationId === destinationEndpointId,
          );
          this.informations.set(matchedInformations);
          const matchedIntegrationIds = [...new Set(matchedInformations.map(i => i.integrationId))];

          var runTimes: IntegrationRunTime[] = [];
          var pushBacks: IntegrationPushBack[] = [];

          this.pushBackService.getPushBackDataByIntegrations(matchedIntegrationIds)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe({
              next: (pushBackResponse) => {
                var filteredRunTimes = pushBackResponse.runTimes.filter(rT => rT.destinationEndpointId === destinationEndpointId);
                runTimes = [...runTimes, ...filteredRunTimes];
                filteredRunTimes.forEach(runTime => {
                  pushBacks = [...pushBacks, ...pushBackResponse.pushBacks.filter(pB => pB.integrationRunTimeId == runTime.integrationRunTimeId)];
                });
                this.runTimes.set(runTimes);
                this.pushBacks.set(pushBacks);
                this.gettingData.set(false);
              },
              error: (_error) => {
                this.toastService.toastCreate('Failed to load integration Pushbacks for ' + information.integrationName, 'Danger', {
                  autoClose: false,
                });
                this.gettingData.set(false);
              },
            });
        },
        error: (_error) => {
          this.toastService.toastCreate('Failed to load Client Pushbacks for ' + clientInformationId, 'Danger', {
            autoClose: false,
          });
          this.gettingData.set(false);
        },
      });
  }

  //#region Url-Setup

  clearToStart(): void {
    this.selectedDestination.set(null);
    this.activeInformation.set(null);
    this.activeClientInformationId.set(null);
    this.identifierIdValue.set(null);
    this.destinationName.set(null);
    this.identifierNameValue.set(null);
    this.gettingData.set(false);
    this.selectedPushBack = null;
    this.selectedRunTime = null;
    this.activeIntegration.set(null);
    this.pushBacks.set(null);
    this.runTimes.set(null);
    this.informations.set(null);
    this.searchedByIdentifier.set(false);

    this.clientDataReady.set(false);
  }

  private setupLocation(clientInfoId: string, dest: string = null, id: string = null, name: string = null): void {
    const clientId = encodeURI(clientInfoId).replace('(', '%28').replace(')', '%29');
    if (dest && id && name) {
      const destStr = encodeURI(dest).replace('(', '%28').replace(')', '%29');
      const idStr = encodeURI(id).replace('(', '%28').replace(')', '%29');
      const nameStr = encodeURI(name).replace('(', '%28').replace(')', '%29');
      window.history.replaceState({}, '', `/client-pushbacks/${destStr}/${clientId}/${idStr}/${nameStr}`);
    }
    else {
      window.history.replaceState({}, '', `/client-pushbacks/${clientId}`);
    }
  }

  //#endregion

  //#region Select Client-Identifier-Integration

  selectDestination(event: any): void {
    if (event == null) {
      this.activeInformation.set(null);
      this.clientDataReady.set(false);
      return;
    }
    this.selectedDestination.set(event);
  }

  public clientSelected(client: ClientInformation): void {
    if (client == null) {
      this.activeInformation.set(null);
      this.clientDataReady.set(false);
      return;
    }

    this.activeClientInformationId.set(client.clientInformationId);
  }

  public infoSelected(id: IntegrationInformation): void {
    this.activeInformation.set(id);
  }

  public informationSelected(id: IntegrationInformation): void {

    if (id == null) {
      this.activeInformation.set(null);
      this.clientDataReady.set(false);
      return;
    }

    this.activeInformation.set(id);
    
    this.resolveClientFromInformation(this.activeInformation());
  }

  public integrationSelected(integration: Integration): void {

    if (integration == null) {
      this.activeIntegration.set(null);
      this.clientDataReady.set(false);
      return;
    }

    this.activeIntegration.set(integration);

    this.activeClientInformationId.set(integration.clientInformationId);
    this.loadPushbacksByIntegration();
  }

  private resolveInformationFromId(clientInformationId: string): void {
    this.idService.getIntegrationInformationsByClientInformation(clientInformationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (informations) => {
          var information = informations.filter(id =>
            id.identifierIdValue === this.identifierIdValue() &&
            id.identifierNameValue === this.identifierNameValue() &&
            id.destinationName === this.destinationName(),
          )[0];
          this.activeInformation.set(information);
          this.loadPushbacks();
        },
        error: (_error) => {
          this.toastService.toastCreate('Failed to get Integration Information By Client Information Id', 'Danger', {
            autoClose: false,
          });
        },
      });
  }

  private resolveClientFromInformation(information: IntegrationInformation): void {
    if (information.clientInformationId !== null && information.clientInformationId !== undefined) {
        this.activeClientInformationId.set(information.clientInformationId);
        this.activeInformation.set(information);
        this.loadPushbacks();
    } else {
      this.clientService.getIntegrationById(information.integrationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (integration) => {
          this.activeClientInformationId.set(integration.clientInformationId);
          this.activeInformation.set(information);
          this.loadPushbacks();
        },
        error: (_error) => {
          this.toastService.toastCreate('Failed to get Integration By Id', 'Danger', {
            autoClose: false,
          });
        },
      });
    }
    
  }

  setEditingIntegrationPushBack(integrationPushBack: IntegrationPushBack = null): void {
    if (integrationPushBack != null) {
      this.selectedPushBack = integrationPushBack;
      this.selectedRunTime = this.runTimes().find(runtime => runtime.activePushBack == true);
      this.dialog.open(this.editPushBackDialog);
    }
  }

  setDeletingIntegrationPushBack(integrationPushBack: IntegrationPushBack = null): void {
    if (integrationPushBack != null) {
      this.selectedPushBack = integrationPushBack;
      this.selectedRunTime = this.runTimes().find(runtime => runtime.activePushBack == true);
      this.dialog.open(this.deletePushBackDialog);
    }
  }

  deleteIntegrationPushBack(): void {
    this.gettingData.set(true);
    this.pushBackService.deleteActiveIntegrationPushBack(this.selectedPushBack.integrationRunTimeId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: () => {
          this.pushBacks.set(this.pushBacks().filter(pushback => pushback.integrationPushBackId != this.selectedPushBack.integrationPushBackId));
          this.gettingData.set(false);

          var integrationName = null;
          if (this.activeIntegration() != null) {
            integrationName = this.activeIntegration().name;
          }
          else {
            integrationName = this.activeInformation().integrationName;
          }

          this.toastService.toastCreate('Successfully Deleted Active PushBack for ' + integrationName + ' runtime: ' + this.selectedRunTime.importTypeDescription + ' ' + this.selectedRunTime.destinationEndpointName + '.', 'Success', {
            autoClose: false,
          });
        },
        error: (_error) => {
          this.gettingData.set(false);
          this.toastService.toastCreate('Failed to delete the pushback for ' + this.activeIntegration().name + ' runtime: ' + this.selectedRunTime.importTypeDescription + ' ' + this.selectedRunTime.destinationEndpointName + '.', 'Danger', {
            autoClose: false,
          });
        },
      });
  }


  integrationPushBackUpdated(pushBack: any): void {
    this.pushBacks.set(this.pushBacks().filter(pb => pb.integrationPushBackId != pushBack.integrationPushBackId));
    this.pushBacks.set(this.pushBacks().concat(pushBack));

    this.dialog.closeAll();
  }

  //#endregion
}
